import { useParams, useNavigate, useLocation, } from "react-router-dom";
import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { ArrowLeft, Trash2, SquareArrowOutUpRight ,} from "lucide-react";
import { formatDateDDMMYYYY, toProperCase } from "../../../Utils";
import {
  GenericTable,
  SearchableDropdown,
  RoleValidator,
} from "../../../Components/Common";
import { useGRN } from "../Hooks/useGRN";
import { useProductMaster } from "../../Inventory/Hooks/useProductMaster";
import {
  GRN_DETAIL_COLUMNS,
  GRN_DETAIL_LABELS,
  GRN_MODES,
  GRNMaterial_Condition,
  GRN_MATERIAL_COLUMNS,
  GRN_MATERIAL_LABELS,
  getGRNStatusColor,
  GRN_MATERIAL_DEFAULT_VALUES,
  GRN_STATUS_NAMES,
} from "./constant";
import { ROLES } from "../../../Constants/Strings";
import { GRN_LIST ,VIEW, EDIT,} from "../../../routes";

const GRNDetails = () => {
  const param = useParams();
  const location = useLocation();
  const [mode, setMode] = useState(GRN_MODES.VIEW);
  const navigate = useNavigate();
  const [grn, setGrn] = useState(null);
  const [notes, setNotes] = useState("");
  const [grnMaterials, setGrnMaterials] = useState([]);
  const [adding, setAdding] = useState(false);

  const { fetchProducts } = useProductMaster();
  const { fetchGRNById, updateGRN } = useGRN();

  // 🔹 Fetch GRN details
  const fetchGRNDetails = async () => {
    const grnData = await fetchGRNById(param.instanceId);
    if (grnData) {
      setGrn(grnData);

      const formattedMaterials =
        grnData.materials?.map((m) => ({
          id: m.id,
          product_id: m.product,
          product_name: m.product_name,
          specification: m.specification,
          UOM: m.UOM,
          quantity: m.quantity,
          product_condition:
            m.product_condition || GRN_MATERIAL_DEFAULT_VALUES.product_condition,
          comments: m.comments || "",
        })) || [];

      setGrnMaterials(formattedMaterials);
    }
  };

  // 🔹 Handle GRN submission (manual call on save/approve)
  const handleGRNSubmit = async (newStatus) => {
    const payload = {
      notes: notes || grn.notes,
      warehouse: grn.warehouse,
      mrr: grn.mrr_detail?.id || "-",
      status: newStatus,
      materials: grnMaterials.map((m) => ({
        ...(m.id && { id: m.id }),
        product: m.product_id,
        quantity: m.quantity,
        product_condition: m.product_condition,
        comments: m.comments,
      })),
    };

    const res = await updateGRN(param.instanceId, payload);

    if (res) {
      toast.success(`GRN ${toProperCase(newStatus)} successfully`);
      setGrn((prev) => ({
        ...prev,
        status: newStatus,
      }));
      navigate(GRN_LIST);
    }
  };

  useEffect(() => {
    if (!param.instanceId) return;

    if (location.pathname.includes(EDIT)) {
      setMode(GRN_MODES.EDIT);
      fetchGRNDetails(param.instanceId);
    } else if (location.pathname.includes(VIEW)) {
      setMode(GRN_MODES.VIEW);
      fetchGRNDetails(param.instanceId);
    }
  }, [param.instanceId, location.pathname]);


  // Delete GRN
  const handleGRNMaterial = (product) => {
    if (grnMaterials.some((m) => m.product_id === product.id)) return;

    setGrnMaterials((prev) => [
      ...prev,
      {
        ...GRN_MATERIAL_DEFAULT_VALUES,
        product_id: product.id,
        product_name: product.product_name,
        specification: product.specification,
        UOM: product.UOM_name,
      },
    ]);
  };
  const removeMaterial = (material) => {
    setGrnMaterials((prev) =>
      prev.filter((m) => m.product_id !== material.product_id)
    );
  };

  const renderFields = (key, row) => {
    switch (key) {
      case "product_name":
        return (
          <div className="flex flex-col">
            <span className="font-semibold text-gray-900">{row.product_name || "-"}</span>
            <span className="text-xs text-gray-500">{row.specification || "-"}</span>
            <span className="text-xs text-gray-500 mt-0.5">{row.UOM || "-"}</span>
          </div>
        );
      case "quantity":
        return mode === GRN_MODES.VIEW ? (
          row.quantity || "-"
        ) : (
          <input
            type="number"
            min="1"
            value={row.quantity}
            onChange={(e) =>
              setGrnMaterials((prev) =>
                prev.map((m) =>
                  m.id === row.id
                    ? {
                        ...m,
                        quantity: Number(e.target.value),
                      }
                    : m
                )
              )
            }
            className="w-28 h-8 px-1 text-sm border border-gray-300 rounded"
          />
        );
      case "product_condition":
        return mode === GRN_MODES.VIEW ? (
          row.product_condition || "-"
        ) : (
          <select
            value={row.product_condition || GRN_MATERIAL_DEFAULT_VALUES.product_condition}
            onChange={(e) =>
              setGrnMaterials((prev) =>
                prev.map((m) =>
                  m.id === row.id
                    ? {
                        ...m,
                        product_condition: e.target.value,
                      }
                    : m
                )
              )
            }
            className="w-36 h-8 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {GRNMaterial_Condition.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      case "comments":
        return mode === GRN_MODES.VIEW ? (
          row.comments || "-"
        ) : (
          <input
            type="text"
            value={row.comments || ""}
            onChange={(e) =>
              setGrnMaterials((prev) =>
                prev.map((m) =>
                  m.id === row.id ? { ...m, comments: e.target.value } : m
                )
              )
            }
            placeholder="Enter comments"
            className="w-32 h-6 text-sm border-b-2 border-gray-300 focus:outline-none"
          />
        );
      case "actions":
        return (
          <button
            onClick={() => removeMaterial(row)}
            className="text-red-500 hover:text-red-700"
            title="Remove"
          >
            <Trash2 size={18} />
          </button>
        );
      default:
        return row[key] || "-";
    }
  };

  const renderActionButtons = () => {
    const { status } = grn;

    // Draft or Editable (SE & PE)
    if (mode !== GRN_MODES.VIEW) {
      const isPending =
        status === GRN_STATUS_NAMES.PENDING_FOR_APPROVAL ||
        status === GRN_STATUS_NAMES.APPROVED ||
        status === GRN_STATUS_NAMES.REJECTED ||
        status === GRN_STATUS_NAMES.COMPLETED;

      return (
        <div className="flex justify-end gap-4 mt-6">
          {/* Save as Draft */}
          <RoleValidator
            allowedRoles={[ROLES.SITE_ENGINEER, ROLES.PROJECT_ENGINEER]}
            mode={isPending ? "disable" : "enable"}
          >
            <button
              onClick={() => handleGRNSubmit(GRN_STATUS_NAMES.DRAFT)}
              disabled={adding || isPending}
              className={`px-4 py-2 rounded-lg font-medium ${adding || isPending
                  ? "bg-gray-300 opacity-50 cursor-not-allowed"
                  : "bg-gray-200 hover:bg-gray-300"
                }`}
            >
              Save as Draft
            </button>
          </RoleValidator>

          {/* Submit for Approval */}
          <RoleValidator
            allowedRoles={[ROLES.SITE_ENGINEER, ROLES.PROJECT_ENGINEER]}
            mode={isPending ? "disable" : "enable"}
          >
            <button
              onClick={() =>
                handleGRNSubmit(GRN_STATUS_NAMES.PENDING_FOR_APPROVAL)
              }
              disabled={adding || isPending}
              className={`px-4 py-2 rounded-lg font-medium text-white ${adding || isPending
                  ? "bg-blue-400 opacity-50 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
                }`}
            >
              Submit for Approval
            </button>
          </RoleValidator>
        </div>
      );
    }

    // For Approver (Project Manager)
    if (
      mode === GRN_MODES.VIEW &&
      status === GRN_STATUS_NAMES.PENDING_FOR_APPROVAL
    ) {
      return (
        <div className="flex justify-end gap-4 mt-6">
          <RoleValidator allowedRoles={[ROLES.PROJECT_MANAGER]}>
            <button
              onClick={() => handleGRNSubmit(GRN_STATUS_NAMES.APPROVED)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Approve
            </button>
          </RoleValidator>
          <RoleValidator allowedRoles={[ROLES.PROJECT_MANAGER]}>
            <button
              onClick={() => handleGRNSubmit(GRN_STATUS_NAMES.REJECTED)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Reject
            </button>
          </RoleValidator>
        </div>
      );
    }

    // After Approval → Mark as Complete (SE & PE)
    if (mode === GRN_MODES.VIEW && status === GRN_STATUS_NAMES.APPROVED) {
      return (
        <div className="flex justify-end mt-6">
          <RoleValidator
            allowedRoles={[ROLES.SITE_ENGINEER, ROLES.PROJECT_ENGINEER]}
          >
            <button
              onClick={() => handleGRNSubmit(GRN_STATUS_NAMES.COMPLETED)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Mark as Complete
            </button>
          </RoleValidator>
        </div>
      );
    }

    return null;
  };

  if (!grn)
    return <div className="p-6 text-center text-gray-600">GRN not found</div>;

  return (
    <div className="min-h-[70vh] p-4">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex gap-4 items-center">
          <span
              onClick={() => navigate(GRN_LIST)}
              className="cursor-pointer text-blue-600 hover:text-blue-800"
          >
            <ArrowLeft size={20} />
          </span>
          <h2 className="text-xl font-semibold text-gray-800">GRN Details</h2>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 md:grid-cols-[40%_60%] gap-6">
        {/* Left Column — GRN Info */}
        <div className="pr-4 border-r border-gray-400">
          <h3 className="text-lg font-semibold mb-6 text-center">
            GRN Information
          </h3>
          <div className="grid grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <p className="text-sm text-gray-500">GRN Code</p>
              <p className="font-semibold text-gray-800 mt-0.5">
                {grn.grn_code}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Warehouse</p>
              <p className="font-semibold text-gray-800 mt-0.5">
                {grn.warehouse_detail?.name || "-"}
              </p>
            </div>
            <div className="flex flex-col">
              <div className="flex items-center gap-1">
                <p className="text-sm text-gray-500">MRR Code </p>
                {grn.mrr_detail?.id && (
                  <SquareArrowOutUpRight
                    size={16}
                    className="text-blue-600 cursor-pointer hover:text-blue-800 transition-colors"
                    onClick={() => navigate(`/material-requests/${grn.mrr_detail.id}`)}
                  />
                )}
              </div>
              <p className="font-semibold text-gray-800 mt-0.5">
                {grn.mrr_detail?.mrr_code || "-"}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Raised By</p>
              <p className="font-semibold text-gray-800 mt-0.5">
                {grn.created_by || "-"}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Raised On</p>
              <p className="font-semibold text-gray-800 mt-0.5">
                {formatDateDDMMYYYY(grn.created?.split("T")[0] || "-")}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <span
                className={`inline-block mt-0.5 px-2 py-1 text-xs font-semibold rounded-full ${getGRNStatusColor(grn.status)}`}
              >
                {grn.status ? toProperCase(grn.status) : "-"}
              </span>
            </div>
            <div className="col-span-2 w-full overflow-x-auto">
              <p className="text-sm text-gray-500">Notes</p>
              {mode === "view" ? (
                <div className="mt-0.5 bg-gray-50 px-2 py-3 rounded-lg border border-gray-100 max-w-full">
                  <p className="font-medium text-gray-800 whitespace-pre-wrap">
                    {grn.notes || "-"}
                  </p>
                </div>
              ) : (
                <textarea
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Write note..."
                  className="w-full border border-gray-300 px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none resize-none"
                />
              )}
            </div>
          </div>
        </div>

        {/* Right Column — Materials */}
        <section className="bg-white p-4">
          <h3 className="text-lg font-semibold mb-4 text-center">Materials</h3>

          {mode === "edit" && (
            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-1">
                Product
              </label>
              <SearchableDropdown
                fetchFunction={fetchProducts}
                onSelect={handleGRNMaterial}
                labelKey="product_name"
                placeholder="Select products"
              />
            </div>
          )}

          {grnMaterials.length > 0 ? ( 
            <div className="overflow-x-auto">
              <GenericTable
                tableColumns={
                  mode === GRN_MODES.VIEW
                    ? GRN_DETAIL_COLUMNS
                    : GRN_MATERIAL_COLUMNS
                }
                tableRows={grnMaterials}
                columnLabels={
                  mode === GRN_MODES.VIEW
                    ? GRN_DETAIL_LABELS
                    : GRN_MATERIAL_LABELS
                }
                renderField={renderFields}
                enableMobileCardView={false}
              />
            </div>
          ) : (
            <p className="text-gray-600 text-center py-6">No materials found</p>
          )} 
          {renderActionButtons()}
        </section>
      </div>
    </div>
  );
};
export default GRNDetails;
